// The status page's reading of /release-feed.json (KPR-791), the public
// release feed the web build writes from the deploy ledgers
// (scripts/build-release-feed.mjs). Pure, so the page and its tests share one
// definition of "a release we can show".

/** How many releases the status page shows. */
export const RECENT_LIMIT = 5;

/** The words a component id reads as on the page. */
export const COMPONENT_NAMES = Object.freeze({
	api: 'API',
	web: 'web app',
});

const isText = value => typeof value === 'string' && value.trim() !== '';

/**
 * The feed as the page shows it, or null when it is not the feed's shape (so
 * the page says the changes could not be read rather than guessing). Only the
 * four fields the page reads survive; an entry missing one is dropped. The
 * newest RECENT_LIMIT entries are kept, in the feed's order (newest first).
 */
export function normalizeReleaseFeed(body) {
	if (!body || typeof body !== 'object' || !Array.isArray(body.releases)) return null;
	return body.releases
		.filter(entry => entry && typeof entry === 'object'
			&& isText(entry.date) && /^\d{4}-\d{2}-\d{2}$/.test(entry.date)
			&& isText(entry.version)
			&& Array.isArray(entry.components) && entry.components.every(isText)
			&& Array.isArray(entry.lines) && entry.lines.some(isText))
		.slice(0, RECENT_LIMIT)
		.map(entry => ({
			date: entry.date,
			version: entry.version.trim(),
			components: entry.components.map(c => c.trim()),
			lines: entry.lines.filter(isText).map(line => line.trim()),
		}));
}

/** "16 Oct 2026" — the release's day, read as the day it is (UTC), never shifted by the reader's zone. */
export function formatReleaseDate(date) {
	const [y, m, d] = date.split('-').map(Number);
	return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** "web app" / "API and web app". */
export function componentLabel(components) {
	const names = components.map(id => COMPONENT_NAMES[id] || id);
	return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || '';
}
